import React, { useEffect, useState } from "react";
import { Loader2, Upload, Trash2, CheckCircle2, Flag, RotateCcw, Link2, FolderInput, CheckSquare, Square, Folder, SendToBack } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const GREEN = "#2F4A2A";
const BERRY = "#6E1E3A";
const GOLD = "#C49247";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function BankAuditBoard() {
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [folders, setFolders] = useState([]);
  const [folderCounts, setFolderCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");
  const [folder, setFolder] = useState("");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [selected, setSelected] = useState([]);

  const load = async (f = filter, fol = folder) => {
    setLoading(true);
    try {
      const params = {};
      if (f !== "all") params.status = f;
      if (fol) params.folder = fol;
      const r = await api.get("/console/bank-audit", { params });
      setRows(r.data.transactions || []);
      setSummary(r.data.summary || {});
      setFolders(r.data.folders || []);
      setFolderCounts(r.data.folder_counts || {});
      setSelected([]);
    } catch { toast.error("Couldn't load transactions."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doImport = async () => {
    if (!raw.trim()) { toast.error("Paste your bank export first."); return; }
    setBusy("import");
    try {
      const r = await api.post("/console/bank-audit/import", { raw });
      toast.success(`Imported ${r.data.imported} transaction(s)${r.data.skipped_duplicates ? `, skipped ${r.data.skipped_duplicates} duplicate(s)` : ""}.`);
      setRaw("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setBusy(""); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSelected(allSelected ? [] : rows.map((r) => r.id));

  const bulk = async (action, extra = {}) => {
    if (!selected.length) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.length} selected transaction(s)? This can't be undone.`)) return;
    setBusy("bulk");
    try {
      const r = await api.post("/console/bank-audit/bulk", { ids: selected, action, ...extra });
      toast.success(action === "delete" ? `Deleted ${r.data.deleted}.` : action === "folder" ? (extra.folder ? `Filed ${r.data.updated} into "${extra.folder}".` : `Removed ${r.data.updated} from folder.`) : `Updated ${r.data.updated}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed."); }
    finally { setBusy(""); }
  };

  const fileIntoFolder = () => {
    const name = window.prompt("File the selected transactions into which folder?\n(e.g. \"July\", \"Wholesale\", \"To chase\")");
    if (name === null) return;
    bulk("folder", { folder: name.trim() });
  };

  const setStatus = async (t, status, extra = {}) => {
    setBusy(t.id);
    try {
      await api.patch(`/console/bank-audit/${t.id}`, { status, note: noteDrafts[t.id] ?? t.note ?? "", ...extra });
      await load();
    } catch { toast.error("Couldn't update."); } finally { setBusy(""); }
  };

  const sendToQueue = async (t) => {
    setBusy(t.id);
    try {
      const r = await api.post(`/console/bank-audit/${t.id}/queue`);
      const matched = r.data.match_status === "matched";
      toast.success(matched ? `Sent to queue and matched to ${r.data.matched_customer_name}.` : "Sent to queue for manual review.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send to the queue."); }
    finally { setBusy(""); }
  };

  const matchToOrder = async (t, order) => {
    const alsoPay = order.payment_status !== "paid" &&
      window.confirm(`Match this $${Number(t.amount).toFixed(2)} payment to ${order.customer_name}'s order AND mark that order as PAID?\n\nCancel = match only, leave order unpaid.`);
    setBusy(t.id);
    try {
      if (alsoPay) {
        await api.patch(`/console/orders/${order.id}`, { payment_status: "paid" });
        toast.success(`${order.customer_name}'s order marked paid.`);
      }
      await api.patch(`/console/bank-audit/${t.id}`, { status: "matched", matched_order_id: order.id, note: `Matched to ${order.customer_name} (${order.id.slice(0, 8)})` });
      await load();
    } catch { toast.error("Couldn't match."); } finally { setBusy(""); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}" (${money(t.amount)})?`)) return;
    setBusy(t.id);
    try { await api.delete(`/console/bank-audit/${t.id}`); await load(); }
    catch { toast.error("Couldn't delete."); } finally { setBusy(""); }
  };

  const FILTERS = [
    { v: "all", label: "All" },
    { v: "unchecked", label: `Unchecked (${summary.unchecked ?? 0})` },
    { v: "matched", label: `Matched (${summary.matched ?? 0})` },
    { v: "flagged", label: `Flagged (${summary.flagged ?? 0})` },
    { v: "quarantined", label: `Quarantined (${summary.quarantined ?? 0})` },
  ];
  const verifyAmount = async (t, amount) => {
    try { await api.post(`/console/bank-audit/${t.id}/verify-amount`, { amount }); toast.success(`Amount confirmed as ${money(amount)}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm that amount."); }
  };

  return (
    <div className="space-y-5" data-testid="bank-audit-board">
      <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }}>
        <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">
          Paste your bank export (CSV or copied rows , each line needs a date and an amount)
        </label>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={5}
          placeholder={"12/07/2026, J SMITH TUBERS, 45.00\n13/07/2026, TRANSFER M BROWN, 120.50\n…straight from your bank's CSV export or copy-paste"}
          className="mt-2 w-full px-3 py-2 border rounded font-mono text-xs"
          style={{ borderColor: EDGE, backgroundColor: CREAM }}
          data-testid="bank-paste"
        />
        <button type="button" onClick={doImport} disabled={busy === "import"} data-testid="bank-import"
          className="mt-3 h-10 px-5 rounded text-white text-sm inline-flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: BERRY }}>
          {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import transactions
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {FILTERS.map((f) => (
          <button key={f.v} type="button" onClick={() => { setFilter(f.v); load(f.v, folder); }} data-testid={`bank-filter-${f.v}`}
            className="px-4 h-9 rounded-full text-xs font-semibold border-2 transition-colors"
            style={filter === f.v ? { backgroundColor: AUBERGINE, borderColor: AUBERGINE, color: "#fff" } : { borderColor: EDGE, backgroundColor: "#fff", color: AUBERGINE }}>
            {f.label}
          </button>
        ))}
        {folders.length > 0 && <span className="w-px h-6 mx-1" style={{ backgroundColor: EDGE }} />}
        {folders.length > 0 && (
          <button type="button" onClick={() => { setFolder(""); load(filter, ""); }} data-testid="bank-folder-all"
            className="px-3 h-9 rounded-full text-xs font-semibold border-2 inline-flex items-center gap-1.5"
            style={!folder ? { backgroundColor: GOLD, borderColor: GOLD, color: "#fff" } : { borderColor: EDGE, backgroundColor: "#fff", color: "#7A5A1E" }}>
            <Folder className="w-3.5 h-3.5" /> All folders
          </button>
        )}
        {folders.map((f) => (
          <button key={f} type="button" onClick={() => { setFolder(f); load(filter, f); }} data-testid={`bank-folder-${f}`}
            className="px-3 h-9 rounded-full text-xs font-semibold border-2 inline-flex items-center gap-1.5"
            style={folder === f ? { backgroundColor: GOLD, borderColor: GOLD, color: "#fff" } : { borderColor: EDGE, backgroundColor: "#fff", color: "#7A5A1E" }}>
            <Folder className="w-3.5 h-3.5" /> {f} ({folderCounts[f] || 0})
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-md border-2 bg-white" style={{ borderColor: BERRY }} data-testid="bank-bulk-bar">
          <span className="text-sm font-semibold" style={{ color: BERRY }}>{selected.length} selected</span>
          <button type="button" onClick={fileIntoFolder} disabled={busy === "bulk"} data-testid="bank-bulk-folder"
            className="h-8 px-3 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1.5" style={{ backgroundColor: GOLD }}>
            <FolderInput className="w-3.5 h-3.5" /> File into folder
          </button>
          <button type="button" onClick={() => bulk("folder", { folder: "" })} disabled={busy === "bulk"} data-testid="bank-bulk-unfile"
            className="h-8 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5" style={{ borderColor: AUBERGINE, color: AUBERGINE }}>
            Remove from folder
          </button>
          <button type="button" onClick={() => bulk("status", { status: "matched" })} disabled={busy === "bulk"} data-testid="bank-bulk-matched"
            className="h-8 px-3 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1.5" style={{ backgroundColor: GREEN }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark matched
          </button>
          <button type="button" onClick={() => bulk("delete")} disabled={busy === "bulk"} data-testid="bank-bulk-delete"
            className="h-8 px-3 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1.5" style={{ backgroundColor: "#A33030" }}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          {busy === "bulk" && <Loader2 className="w-4 h-4 animate-spin" style={{ color: BERRY }} />}
        </div>
      )}

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: EDGE }}>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center italic opacity-60" data-testid="bank-empty">No transactions here. Paste a bank export above ↑</div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-4 py-2 border-b text-xs font-semibold" style={{ borderColor: EDGE, backgroundColor: CREAM, color: AUBERGINE }}>
              <button type="button" onClick={toggleAll} className="inline-flex items-center gap-1.5" data-testid="bank-select-all">
                {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 opacity-50" />}
                Select all ({rows.length})
              </button>
            </div>
          <ul className="divide-y" style={{ borderColor: EDGE }}>
            {rows.map((t) => (
              <li key={t.id} className="p-4" style={{ opacity: busy === t.id ? 0.5 : 1, backgroundColor: selected.includes(t.id) ? "#FBF6EA" : "transparent" }} data-testid={`bank-txn-${t.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <button type="button" onClick={() => toggleSel(t.id)} className="mt-0.5 shrink-0" data-testid={`bank-select-${t.id}`}>
                    {selected.includes(t.id) ? <CheckSquare className="w-5 h-5" style={{ color: BERRY }} /> : <Square className="w-5 h-5 opacity-40" />}
                  </button>
                  <div className="w-24 shrink-0 text-sm whitespace-nowrap">{t.date}</div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-sm font-semibold">
                      {t.name}
                      {t.folder && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: "#F7EBD4", color: "#7A5A1E" }} data-testid={`bank-folder-tag-${t.id}`}><Folder className="w-2.5 h-2.5" />{t.folder}</span>}
                    </div>
                    {t.status === "unchecked" && t.suggestions?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {t.suggestions.map((o) => (
                          <button key={o.id} type="button" onClick={() => matchToOrder(t, o)} data-testid={`bank-suggest-${t.id}-${o.id}`}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border mr-1.5 hover:bg-[#FBF6EA]"
                            style={{ borderColor: GOLD, color: "#7A5A1E" }}>
                            <Link2 className="w-3 h-3" />
                            Possible: {o.customer_name} · {money(o.total)} · {o.payment_status}
                          </button>
                        ))}
                      </div>
                    )}
                    {(t.status !== "unchecked" || t.note) && (
                      <div className="text-xs opacity-70 mt-1 italic">{t.note}</div>
                    )}
                    {(t.status === "quarantined" || t.needs_amount_check) && (
                      <div className="mt-1.5 border border-[#A33030]/40 bg-[#FFF4F0] rounded p-2 text-xs" data-testid={`bank-quarantine-${t.id}`}>
                        <strong className="text-[#A33030]">Quarantined , which column is the payment?</strong> The source line had {(t.amount_candidates || []).length} numbers (the last is usually the running balance). Nothing can be matched or loaded until you confirm.
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {(t.amount_candidates || [t.amount]).map((c, i) => (
                            <button key={i} type="button" onClick={() => verifyAmount(t, c)} data-testid={`bank-verify-${t.id}-${i}`} className="px-2.5 py-1 rounded border border-[#3E1F46] text-[#3E1F46] bg-white text-xs font-semibold">{money(c)}{i === (t.amount_candidates || []).length - 1 && (t.amount_candidates || []).length > 1 ? " (balance?)" : ""}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {t.status === "unchecked" && (
                      <input
                        placeholder="Note (optional)…"
                        value={noteDrafts[t.id] ?? ""}
                        onChange={(e) => setNoteDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                        className="mt-1.5 w-full max-w-sm h-8 px-2.5 border rounded text-xs"
                        style={{ borderColor: EDGE, backgroundColor: CREAM }}
                        data-testid={`bank-note-${t.id}`}
                      />
                    )}
                  </div>
                  <div className="text-right font-semibold whitespace-nowrap" style={{ color: t.amount < 0 ? "#A33030" : GREEN }}>
                    {money(t.amount)}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button type="button" title={t.queue_id ? "Already in Bank Auditor Queue" : "Send to Bank Auditor Queue"}
                      onClick={() => sendToQueue(t)} disabled={Boolean(t.queue_id)} data-testid={`bank-send-queue-${t.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-55"
                      style={{ borderColor: GOLD, color: "#7A5A1E", backgroundColor: t.queue_id ? CREAM : "#fff" }}>
                      <SendToBack className="w-3.5 h-3.5" /> {t.queue_id ? "In queue" : "Send to queue"}
                    </button>
                    {t.status === "unchecked" ? (
                      <>
                        <button type="button" title="Mark matched / checked" onClick={() => setStatus(t, "matched")} data-testid={`bank-match-${t.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: GREEN }}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Matched
                        </button>
                        <button type="button" title="Flag an issue" onClick={() => setStatus(t, "flagged")} data-testid={`bank-flag-${t.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "#A33030", color: "#A33030" }}>
                          <Flag className="w-3.5 h-3.5" /> Flag
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                          style={{ backgroundColor: t.status === "matched" ? GREEN : "#A33030" }} data-testid={`bank-status-${t.id}`}>
                          {t.status === "matched" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Flag className="w-3.5 h-3.5" />}
                          {t.status}
                        </span>
                        <button type="button" title="Re-open" onClick={() => setStatus(t, "unchecked", { matched_order_id: null })} data-testid={`bank-reopen-${t.id}`}
                          className="p-1.5 rounded hover:bg-[#FBF6EA]" style={{ color: AUBERGINE }}>
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button type="button" title="Delete" onClick={() => remove(t)} data-testid={`bank-delete-${t.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </div>
  );
}
